import React, { useMemo } from 'react'
import { Icon, Text } from '@adc/ui-core'
import classnames from 'classnames/bind'
import type { BuyingCriteriaData, BuyingCriteriaActions } from '@adc/blueprint'
import { useApplicationModals, ModalIds } from '../../auctionApplication/modals'
import BuyingCriteriaContent from '../content/index'
import MatchedPropertiesCard from '../matchedPropertiesCard/index'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface UseBuyingCriteriaModalOptions {
  data: BuyingCriteriaData
  actions: BuyingCriteriaActions
}

export interface UseBuyingCriteriaModalReturn {
  openBuyingCriteriaModal: () => void
  closeBuyingCriteriaModal: () => void
}

/**
 * Opens the full Buying Criteria modal via useApplicationModals.
 *
 * @example
 * const { data, actions } = useBuyingCriteria({ ...options, autoSave: true })
 * const { openBuyingCriteriaModal } = useBuyingCriteriaModal({ data, actions })
 */
export const useBuyingCriteriaModal = (
  options: UseBuyingCriteriaModalOptions
): UseBuyingCriteriaModalReturn => {
  const { data, actions } = options
  const modalProps = useMemo(
    () => ({
      title: (
        <span className={cx('modal-title')}>
          <Icon
            name='location-crosshairs'
            iconStyle='solid'
            color='grayDark'
            style={{ fontSize: 20 }}
          />
          <Text
            as='span'
            size='big'
            weight='heavy'
            color='grayDark'
            style={{ fontWeight: 800 }}
          >
            Refine your buying criteria
          </Text>
        </span>
      ),
      size: 'small',
      elmId: 'buying-criteria-modal',
      children: (
        <div
          className={cx('modal-scroll')}
          data-elm-id='buying-criteria-modal-body'
        >
          <p className={cx('modal-subtitle')}>
            Sharpen your deal matching by telling us exactly what you want.
          </p>
          <MatchedPropertiesCard variant='inline' preferences={data.criteria} />
          <BuyingCriteriaContent data={data} actions={actions} borderless />
        </div>
      ),
      actions: [
        {
          content: 'Done',
          role: 'primary',
          elmId: 'buying-criteria-modal-done',
        },
      ],
    }),
    [data, actions]
  )

  const { open, close } = useApplicationModals(ModalIds.General, modalProps)

  return { openBuyingCriteriaModal: open, closeBuyingCriteriaModal: close }
}
